import {
  type AdminExhibitionPurpose,
  EXHIBITION_LEAD_TIME_NOTE_MAX_LENGTH,
  EXHIBITION_TITLE_MAX_LENGTH,
  type ExhibitionFields,
  exhibitionFieldsSchema,
} from '@repo/api-contracts';
import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';
import { firstErrorPerField } from '@/shared/lib';
import { Button, FileDropField, Input, Label } from '@/shared/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp';
// 목적은 사전 목록에서 고르는 select 라 칸 오류가 날 수 없다 — 글 칸 둘만 검사 결과를 보여 준다.
const FIELD_KEYS = ['title', 'leadTimeNote'] as const;

type FieldErrors = Partial<Record<(typeof FIELD_KEYS)[number], string>>;

/** 새로 고른 대표 이미지와 그 미리보기 URL — 파일만 들고 있으면 렌더마다 URL 을 새로 만들게 된다. */
interface PickedImage {
  readonly file: File;
  readonly previewUrl: string;
}

interface ExhibitionFormModalProps {
  readonly mode: 'create' | 'edit';
  readonly purposes: readonly AdminExhibitionPurpose[];
  /** 추가는 현재 탭의 목적만 채운 빈 값, 수정은 기존 값. */
  readonly initialFields: ExhibitionFields;
  /** 지금 올라가 있는 대표 이미지 — 없으면 null. 새로 고른 파일이 없을 때만 보여 준다. */
  readonly currentImageUrl: string | null;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly onSubmit: (fields: ExhibitionFields, file: File | null) => void;
  readonly onClose: () => void;
}

/** 기획전 추가·수정 모달 — 서버와 같은 계약 스키마로 먼저 검사해 칸별 오류를 보여 준다. */
export const ExhibitionFormModal = ({
  mode,
  purposes,
  initialFields,
  currentImageUrl,
  isPending,
  errorMessage,
  onSubmit,
  onClose,
}: ExhibitionFormModalProps): ReactElement => {
  const [fields, setFields] = useState<ExhibitionFields>(initialFields);
  const [picked, setPicked] = useState<PickedImage | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const title = mode === 'create' ? '기획전 추가' : '기획전 수정';

  // 미리보기 URL 은 브라우저 메모리를 잡는다 — 교체·언마운트 때 해제하려고 최신 값을 ref 에 둔다.
  const previewUrlRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (previewUrlRef.current !== null) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    },
    [],
  );

  const handleImageChange = (nextFile: File | null): void => {
    // 새로 고를 때마다 앞서 만든 URL 을 먼저 해제한다 — 쌓이면 모달을 닫을 때까지 메모리가 샌다.
    if (previewUrlRef.current !== null) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    if (nextFile === null) {
      setPicked(null);
      return;
    }

    const previewUrl = URL.createObjectURL(nextFile);
    previewUrlRef.current = previewUrl;
    setPicked({ file: nextFile, previewUrl });
  };

  const changeField =
    (key: keyof ExhibitionFields) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const { value } = event.target;
      setFields((current) => ({ ...current, [key]: value }));
    };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = exhibitionFieldsSchema.safeParse(fields);

    setFieldErrors(
      parsed.success
        ? {}
        : firstErrorPerField(z.flattenError(parsed.error).fieldErrors, FIELD_KEYS),
    );

    if (parsed.success) {
      onSubmit(parsed.data, picked?.file ?? null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-lg border bg-background p-5"
      >
        <p className="font-medium">{title}</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="exhibition-form-title">기획전 이름</Label>
            <Input
              id="exhibition-form-title"
              value={fields.title}
              maxLength={EXHIBITION_TITLE_MAX_LENGTH}
              onChange={changeField('title')}
              aria-invalid={fieldErrors.title !== undefined}
            />
            {fieldErrors.title !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.title}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="exhibition-form-purpose">목적</Label>
            <select
              id="exhibition-form-purpose"
              value={fields.purposeId}
              onChange={changeField('purposeId')}
              className="h-9 rounded-2xl border border-input bg-background px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {purposes.map((purpose) => (
                <option key={purpose.id} value={purpose.id}>
                  {purpose.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="exhibition-form-lead-time">납기 문구</Label>
            <Input
              id="exhibition-form-lead-time"
              placeholder="예: 영업일 7일"
              value={fields.leadTimeNote}
              maxLength={EXHIBITION_LEAD_TIME_NOTE_MAX_LENGTH}
              onChange={changeField('leadTimeNote')}
              aria-invalid={fieldErrors.leadTimeNote !== undefined}
            />
            {fieldErrors.leadTimeNote !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.leadTimeNote}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="exhibition-form-image">대표 이미지</Label>
            {/* 새로 고른 파일이 있으면 그 미리보기를, 없으면 지금 등록된 이미지를 보여 준다. */}
            {picked !== null ? (
              <img
                src={picked.previewUrl}
                alt="고른 대표 이미지"
                className="aspect-[4/3] w-32 rounded-md object-cover"
              />
            ) : currentImageUrl !== null ? (
              <img
                src={currentImageUrl}
                alt="현재 대표 이미지"
                className="aspect-[4/3] w-32 rounded-md object-cover"
              />
            ) : null}
            <FileDropField
              id="exhibition-form-image"
              accept={ACCEPT}
              buttonLabel="이미지 선택"
              fileName={picked?.file.name ?? null}
              onFiles={([next]) => handleImageChange(next ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              대표 이미지가 있어야 공개로 바꿀 수 있습니다. 나중에 올려도 됩니다.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={isPending}>
              저장
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
